/**
 * SFRDashboard.jsx — Manager/Safety view of all SFR submissions.
 * Embeds in FRMS Module (SFR tab) and auto-polls every 30s.
 */
import { useEffect, useState, useCallback, useRef } from "react";
import {
  AlertTriangle, CheckCircle, BarChart3, TrendingUp,
  Clock, Plane, RefreshCw, Radio,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const SP_COLORS = ["","text-green-400","text-green-400","text-teal-400","text-amber-400","text-orange-400","text-red-400","text-red-500"];
const SP_BG     = ["","bg-green-500","bg-green-400","bg-teal-400","bg-amber-400","bg-orange-400","bg-red-500","bg-red-600"];
const SP_LABEL  = ["","Fully Alert","Lively","Okay","A Little Tired","Mod. Tired","Extremely Tired","Exhausted"];

async function apiFetch(path) {
  const token = localStorage.getItem("fs_token");
  const r = await fetch(`${API}/api/sfr${path}`, { headers: { Authorization: `Bearer ${token}` } });
  return r.json();
}

export default function SFRDashboard() {
  const [dashboard, setDashboard] = useState(null);
  const [liveFeed, setLiveFeed] = useState({ submissions: [], alert_count: 0, fetched_at: "" });
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const pollRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [dash, feed] = await Promise.all([
        apiFetch("/dashboard"),
        apiFetch("/live-feed?limit=20"),
      ]);
      setDashboard(dash);
      setLiveFeed(feed);
      setLastRefresh(new Date().toLocaleTimeString("en-IN"));
    } catch (_) {}
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
    pollRef.current = setInterval(load, 30000); // real-time polling
    return () => clearInterval(pollRef.current);
  }, [load]);

  if (loading) return (
    <div className="text-center py-12 text-muted-foreground text-sm animate-pulse">
      Connecting to SFR live feed...
    </div>
  );

  const noData = !dashboard || dashboard.total_submissions === 0;

  return (
    <div className="space-y-5" data-testid="sfr-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-1.5 px-2.5 py-1.5 bg-green-500/10 border border-green-500/20 rounded-sm">
            <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
            <span className="text-[10px] font-bold text-green-400">LIVE — polls every 30s</span>
          </div>
          {lastRefresh && <span className="text-[10px] text-muted-foreground flex items-center gap-1"><Clock size={9} /> {lastRefresh}</span>}
        </div>
        <button onClick={load}
          className="flex items-center gap-1.5 px-2.5 py-1.5 border border-border text-xs text-muted-foreground hover:text-foreground rounded-sm transition-colors"
          data-testid="sfr-refresh-btn">
          <RefreshCw size={10} /> Refresh
        </button>
      </div>

      {/* KPIs */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[
          { label: "Total Submissions",  value: dashboard?.total_submissions ?? 0, color: "text-primary",    Icon: Radio },
          { label: "Alert Flags (≥5)",   value: dashboard?.alert_count ?? 0,       color: "text-red-400",    Icon: AlertTriangle },
          { label: "Average SFR",        value: dashboard?.avg_sfr ?? "—",         color: "text-amber-400",  Icon: BarChart3 },
          { label: "Live Feed",          value: liveFeed.submissions.length,        color: "text-green-400",  Icon: TrendingUp },
        ].map(({ label, value, color, Icon }) => (
          <div key={label} className="bg-card border border-border rounded-sm p-4">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground">{label}</span>
              <Icon size={13} className={color} />
            </div>
            <div className={`text-2xl font-mono font-black ${color}`}>{value}</div>
          </div>
        ))}
      </div>

      {noData ? (
        <div className="text-center py-12 border border-dashed border-border rounded-sm">
          <Radio size={28} className="text-muted-foreground mx-auto mb-2" />
          <div className="text-sm text-muted-foreground">No SFR submissions yet.</div>
          <div className="text-xs text-muted-foreground mt-1">Pilots submit ratings via <span className="font-mono text-primary">/my-sectors</span> after each sector.</div>
        </div>
      ) : (
        <>
          {/* Recent Alerts */}
          {(dashboard?.recent_alerts || []).length > 0 && (
            <div className="bg-red-500/5 border border-red-500/20 rounded-sm p-4">
              <div className="text-xs font-black uppercase tracking-widest text-red-400 mb-3 flex items-center gap-2">
                <AlertTriangle size={11} /> High-Stress Flags (SFR ≥ 5)
              </div>
              <div className="space-y-2">
                {dashboard.recent_alerts.map(a => (
                  <div key={a.ref} className="flex items-center justify-between p-2.5 bg-background/60 rounded-sm border border-red-500/10">
                    <div>
                      <div className="flex items-center gap-2">
                        <Plane size={11} className="text-muted-foreground" />
                        <span className="text-xs font-bold text-foreground">{a.flight_number}</span>
                        <span className="text-xs text-muted-foreground">{a.route}</span>
                      </div>
                      <div className="text-[10px] text-muted-foreground mt-0.5">{a.pilot_name} · {a.sector_date}</div>
                      {a.stressors?.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1">
                          {a.stressors.slice(0,3).map(s => (
                            <span key={s} className="text-[9px] bg-red-500/10 text-red-400 px-1.5 py-0.5 rounded-sm">{s}</span>
                          ))}
                        </div>
                      )}
                    </div>
                    <div className="flex flex-col items-end gap-1 shrink-0 ml-3">
                      <div className={`w-8 h-8 rounded-sm ${SP_BG[a.sfr_rating]} flex items-center justify-center text-white font-black text-base`}>{a.sfr_rating}</div>
                      <div className={`text-[9px] font-bold ${SP_COLORS[a.sfr_rating]}`}>{SP_LABEL[a.sfr_rating]}</div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Route Breakdown */}
          {(dashboard?.route_breakdown || []).length > 0 && (
            <div className="bg-card border border-border rounded-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-border text-xs font-black uppercase tracking-widest text-muted-foreground">Route Stress Analysis</div>
              <div className="divide-y divide-border/50">
                {dashboard.route_breakdown.map(r => (
                  <div key={r.route} className="flex items-center justify-between px-4 py-3">
                    <div className="flex items-center gap-3">
                      <Plane size={12} className="text-muted-foreground" />
                      <div>
                        <div className="text-sm font-bold text-foreground">{r.route}</div>
                        <div className="text-[10px] text-muted-foreground">{r.count} rating{r.count !== 1 ? "s" : ""}</div>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      {r.alerts > 0 && (
                        <span className="text-[10px] font-bold text-red-400 flex items-center gap-0.5"><AlertTriangle size={9}/> {r.alerts}</span>
                      )}
                      <div className={`w-10 h-10 rounded-sm ${SP_BG[Math.round(r.avg_sfr)] || "bg-muted"} flex items-center justify-center text-white font-black text-sm`}>
                        {r.avg_sfr}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Rating distribution */}
          {dashboard?.rating_distribution && (
            <div className="bg-card border border-border rounded-sm p-4">
              <div className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-3">Rating Distribution</div>
              <div className="flex items-end gap-1.5 h-16">
                {[1,2,3,4,5,6,7].map(n => {
                  const count = dashboard.rating_distribution[String(n)] || 0;
                  const max = Math.max(...Object.values(dashboard.rating_distribution).map(Number), 1);
                  const pct = (count / max) * 100;
                  return (
                    <div key={n} className="flex-1 flex flex-col items-center gap-1">
                      <div className="w-full flex items-end justify-center" style={{ height: 56 }}>
                        <div className={`w-full rounded-t-sm ${SP_BG[n]} transition-all`} style={{ height: `${Math.max(pct, count > 0 ? 8 : 0)}%` }} />
                      </div>
                      <span className={`text-[10px] font-bold ${SP_COLORS[n]}`}>{n}</span>
                      {count > 0 && <span className="text-[9px] text-muted-foreground">{count}</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Top stressors */}
          {dashboard?.stressor_freq && Object.keys(dashboard.stressor_freq).length > 0 && (
            <div className="bg-card border border-border rounded-sm p-4">
              <div className="text-xs font-black uppercase tracking-widest text-muted-foreground mb-3">Top Stressors Reported</div>
              <div className="space-y-2">
                {Object.entries(dashboard.stressor_freq).slice(0,6).map(([stressor, count]) => {
                  const maxCount = Math.max(...Object.values(dashboard.stressor_freq));
                  const pct = (count / maxCount) * 100;
                  return (
                    <div key={stressor}>
                      <div className="flex items-center justify-between text-xs mb-1">
                        <span className="text-foreground">{stressor}</span>
                        <span className="font-mono text-muted-foreground">{count}</span>
                      </div>
                      <div className="h-1.5 bg-background/60 rounded-full">
                        <div className="h-full bg-primary rounded-full transition-all" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Live feed */}
          {liveFeed.submissions.length > 0 && (
            <div className="bg-card border border-border rounded-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-border text-xs font-black uppercase tracking-widest text-muted-foreground flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                Live Submission Feed
              </div>
              <div className="divide-y divide-border/50">
                {liveFeed.submissions.slice(0,8).map(s => (
                  <div key={s.ref} className={`flex items-center justify-between px-4 py-3 ${s.alert_flagged ? "bg-red-500/3" : ""}`} data-testid={`sfr-feed-${s.ref}`}>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-foreground">{s.flight_number}</span>
                        <span className="text-xs text-muted-foreground">{s.route}</span>
                        {s.alert_flagged && <AlertTriangle size={10} className="text-red-400" />}
                      </div>
                      <div className="text-[10px] text-muted-foreground">{s.pilot_name} · {s.sector_date}</div>
                    </div>
                    <div className={`w-8 h-8 rounded-sm ${SP_BG[s.sfr_rating]} flex items-center justify-center text-white font-black text-sm`}>
                      {s.sfr_rating}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
